import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

import {
  CiscoProductSelector,
  GtmSelector,
  ProductVersionSelector,
} from './RagSelects';

type TargetType = 'product' | 'go_to_market';

type Props = {
  open: boolean;
  uploading?: boolean;
  onClose: () => void;
  onUpload: (payload: {
    target_type: TargetType;
    product_id?: number | null;
    go_to_market_id?: number | null;
    files: File[];
    product_version?: string | null;
  }) => Promise<any>;
};

const ACCEPT = [
  '.pdf',
  '.docx',
  '.pptx',
  '.xlsx',
  '.xls',
  '.csv',
  '.txt',
  '.md',
  '.markdown',
  '.html',
  '.htm',
].join(',');

const VERSIONED_PRODUCT_OFFER_TYPES = new Set([
  'appliance_product',
  'hybrid_product',
]);

function sizeLabel(size: number) {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${Math.round(size / (1024 * 1024))} MB`;
}

function normalizeOfferType(product: any) {
  return String(product?.offer_type || product?.offer_type_key || '').trim().toLowerCase();
}

const LocalFileUploadDialog: React.FC<Props> = ({
                                                  open,
                                                  uploading,
                                                  onClose,
                                                  onUpload,
                                                }) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const [targetType, setTargetType] = React.useState<TargetType>('product');
  const [productId, setProductId] = React.useState<number | null>(null);
  const [selectedProduct, setSelectedProduct] = React.useState<any | null>(null);
  const [goToMarketId, setGoToMarketId] = React.useState<number | null>(null);
  const [selectedGtm, setSelectedGtm] = React.useState<any | null>(null);
  const [productVersion, setProductVersion] = React.useState<string | null>(null);
  const [files, setFiles] = React.useState<File[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [lastResult, setLastResult] = React.useState<any>(null);

  const selectedOfferType = normalizeOfferType(selectedProduct);
  const showProductVersionSelect =
    targetType === 'product' &&
    selectedProduct &&
    VERSIONED_PRODUCT_OFFER_TYPES.has(selectedOfferType);

  React.useEffect(() => {
    if (!open) {
      setTargetType('product');
      setProductId(null);
      setSelectedProduct(null);
      setGoToMarketId(null);
      setSelectedGtm(null);
      setProductVersion(null);
      setFiles([]);
      setError(null);
      setLastResult(null);
    }
  }, [open]);

  React.useEffect(() => {
    setError(null);
    setLastResult(null);
    setFiles([]);

    if (targetType === 'product') {
      setGoToMarketId(null);
      setSelectedGtm(null);
    } else {
      setProductId(null);
      setSelectedProduct(null);
      setProductVersion(null);
    }
  }, [targetType]);

  React.useEffect(() => {
    if (!showProductVersionSelect) {
      setProductVersion(null);
    }
  }, [showProductVersionSelect]);

  const addFiles = (fileList: FileList | null) => {
    if (!fileList) return;

    const next = Array.from(fileList);

    setFiles((current) => {
      const existing = new Set(
        current.map((file) => `${file.name}:${file.size}:${file.lastModified}`),
      );

      const merged = [...current];

      next.forEach((file) => {
        const key = `${file.name}:${file.size}:${file.lastModified}`;
        if (!existing.has(key)) {
          merged.push(file);
          existing.add(key);
        }
      });

      return merged;
    });
  };

  const removeFile = (index: number) => {
    setFiles((current) => current.filter((_file, idx) => idx !== index));
  };

  const submit = async () => {
    setError(null);
    setLastResult(null);

    if (targetType === 'product' && !productId) {
      setError('Cisco product is required.');
      return;
    }

    if (targetType === 'go_to_market' && !goToMarketId) {
      setError('Go-To-Market is required.');
      return;
    }

    if (showProductVersionSelect && !productVersion) {
      setError('Product version is required for appliance and hybrid products.');
      return;
    }

    if (!files.length) {
      setError('Select at least one local file.');
      return;
    }

    try {
      const result = await onUpload({
        target_type: targetType,
        product_id: targetType === 'product' ? productId : null,
        go_to_market_id: targetType === 'go_to_market' ? goToMarketId : null,
        files,
        product_version: showProductVersionSelect ? productVersion : null,
      });

      setLastResult(result);
      setFiles([]);
    } catch (err: any) {
      setError(err?.message || 'Upload failed.');
    }
  };

  const totalBytes = files.reduce((sum, file) => sum + file.size, 0);

  return (
    <Dialog open={open} onClose={uploading ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Upload Local Files for RAG Ingestion</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {uploading ? <LinearProgress /> : null}

          <Alert severity="info">
            Files are stored in the doc-export S3 bucket under <b>local-uploads/pending/...</b>.
            Toolbox AI determines the bucket, pending path, converted markdown path, image path, and manifest path.
            Processing converts the file to markdown/assets and updates the canonical manifest for the selected target.
          </Alert>

          {error ? <Alert severity="error">{error}</Alert> : null}

          {lastResult ? (
            <Alert severity="success">
              Upload completed. {Array.isArray(lastResult) ? lastResult.length : 1} file(s) queued for processing.
            </Alert>
          ) : null}

          <TextField
            fullWidth
            select
            label="Upload Target"
            value={targetType}
            onChange={(event) => setTargetType(event.target.value as TargetType)}
            helperText="Choose whether these files belong to a Cisco product or a Go-To-Market."
          >
            <MenuItem value="product">Cisco Product</MenuItem>
            <MenuItem value="go_to_market">Go-To-Market</MenuItem>
          </TextField>

          {targetType === 'product' ? (
            <CiscoProductSelector
              value={productId}
              onChange={(value, row) => {
                setProductId(value);
                setSelectedProduct(row || null);
                setProductVersion(null);
              }}
              required
              label="Cisco Product"
              helperText="Converted local files are merged into this product's canonical manifest."
            />
          ) : null}

          {targetType === 'go_to_market' ? (
            <GtmSelector
              value={goToMarketId}
              onChange={(value, row) => {
                setGoToMarketId(value);
                setSelectedGtm(row || null);
              }}
              required
              label="Go-To-Market"
              helperText="Converted local files are merged into this Go-To-Market's canonical manifest."
            />
          ) : null}

          {selectedProduct && targetType === 'product' ? (
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
              <Typography variant="body2" fontWeight={700}>
                {selectedProduct.name}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                ID: {selectedProduct.id} / Offer: {selectedProduct.offer_type || '-'} / Recommended Version:{' '}
                {selectedProduct.recommended_version || '-'}
              </Typography>
            </Paper>
          ) : null}

          {selectedGtm && targetType === 'go_to_market' ? (
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
              <Typography variant="body2" fontWeight={700}>
                {selectedGtm.name}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                ID: {selectedGtm.id} / Type: {selectedGtm.type || selectedGtm.type_key || '-'}
              </Typography>
            </Paper>
          ) : null}

          {showProductVersionSelect ? (
            <ProductVersionSelector
              productId={productId}
              value={productVersion}
              onChange={setProductVersion}
              required
              helperText="Required for appliance and hybrid products."
            />
          ) : null}

          <Box>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={ACCEPT}
              style={{ display: 'none' }}
              onChange={(event) => addFiles(event.target.files)}
            />

            <Paper
              variant="outlined"
              sx={{
                p: 3,
                textAlign: 'center',
                borderStyle: 'dashed',
                cursor: uploading ? 'not-allowed' : 'pointer',
                bgcolor: 'grey.50',
              }}
              onClick={() => {
                if (!uploading) inputRef.current?.click();
              }}
              onDragOver={(event) => {
                event.preventDefault();
              }}
              onDrop={(event) => {
                event.preventDefault();
                if (!uploading) addFiles(event.dataTransfer.files);
              }}
            >
              <CloudUploadIcon color="primary" />

              <Typography variant="body1" fontWeight={700}>
                Drag files here or click to choose
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Supported: PDF, Word, PowerPoint, Excel, CSV, TXT, Markdown, HTML
              </Typography>

              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                Document titles are inferred automatically from filenames.
              </Typography>
            </Paper>
          </Box>

          {files.length ? (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" variant="outlined" label={`${files.length} selected`} />
                <Chip size="small" variant="outlined" label={sizeLabel(totalBytes)} />
              </Stack>

              <Stack spacing={1}>
                {files.map((file, index) => (
                  <Paper key={`${file.name}-${file.size}-${index}`} variant="outlined" sx={{ p: 1 }}>
                    <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="center">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                          {file.name}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                          {file.type || 'unknown'} / {sizeLabel(file.size)}
                        </Typography>
                      </Box>

                      <Button size="small" color="error" onClick={() => removeFile(index)} disabled={uploading}>
                        Remove
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={uploading}>
          Close
        </Button>

        <Button
          variant="contained"
          onClick={submit}
          disabled={
            uploading ||
            !files.length ||
            (targetType === 'product' && !productId) ||
            (targetType === 'go_to_market' && !goToMarketId) ||
            Boolean(showProductVersionSelect && !productVersion)
          }
        >
          {uploading ? 'Uploading...' : 'Queue Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocalFileUploadDialog;